import type { Person } from '@local/common-lib';
import { Save as SaveIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Card,
  CardActions,
  CardContent,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { DisplayField } from '../../components/display-field/index.js';
import { FormikSwitch } from '../../components/formik-fields/switch.js';
import { MiddleSpinner } from '../../components/middle-spinner/index.js';
import { CenterLayout } from '../../components/page-layout/index.js';
import { useAxios } from '../../hooks/use-axios.js';
import { useSetTitle } from '../../hooks/use-title.js';
import { setAppearanceSettings } from '../../utils/appearance-settings.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import { ErrorPage } from '../error/index.js';

interface ViewProps {
  profile: Person.View | Person.AccountView;
}

interface AppearanceFormValues {
  colorMode: 'dark' | 'light';
  largeText: boolean;
}

export const MyProfilePage: FC<{ view?: FC<ViewProps> }> = ({
  view: View = MyProfileView,
}) => {
  const [profile] = useAxios<Person.View | Person.AccountView>(
    '/api/v1/my/profile',
  );
  useSetTitle('My Profile');

  if (profile.loading) return <MiddleSpinner />;
  if (profile.error) return <ErrorPage error={profile.error} />;

  return <View profile={profile.data} />;
};

export const MyProfileView: FC<ViewProps> = ({ profile }) => {
  const auth = useAuthenticated();
  const { enqueueSnackbar } = useSnackbar();
  const formik = useFormik<AppearanceFormValues>({
    initialValues: {
      colorMode: profile.appearance?.colorMode === 'light' ? 'light' : 'dark',
      largeText: profile.appearance?.largeText ?? false,
    },
    onSubmit: async (values, helpers) => {
      try {
        await auth.axios.patch('/api/v1/my/profile', {
          appearance: {
            colorMode: values.colorMode,
            largeText: values.largeText,
          },
        });

        setAppearanceSettings({
          colorMode: values.colorMode,
          largeText: values.largeText,
        });
        enqueueSnackbar('Appearance settings updated', { variant: 'success' });
      } catch (error) {
        Sentry.captureException(error);
        enqueueSnackbar('Unable to update appearance settings', {
          variant: 'error',
        });
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  return (
    <CenterLayout>
      <Grid container spacing={3}>
        <Grid item md={6}>
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                {'finished' in profile && profile.finished && (
                  <Grid item xs={12}>
                    <Chip
                      label="Finished"
                      size="small"
                      sx={{ backgroundColor: 'red' }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <DisplayField
                    label="Name"
                    value={`${profile.firstName} ${profile.lastName.toUpperCase()}`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DisplayField label="Email address" value={profile.email} />
                </Grid>
                <Grid item xs={12}>
                  <DisplayField label="Phone" value={profile.phone} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item md={6} xs={12}>
          <form onSubmit={formik.handleSubmit}>
            <Card>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel id="color-mode-label">Colour Mode</FormLabel>
                      <RadioGroup
                        aria-labelledby="color-mode-label"
                        name="colorMode"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.colorMode}
                      >
                        <FormControlLabel
                          control={<Radio />}
                          label="Dark (default)"
                          value="dark"
                        />
                        <FormControlLabel
                          control={<Radio />}
                          label="Light"
                          value="light"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormikSwitch
                      formik={formik}
                      id="largeText"
                      label="Use larger text"
                    />
                  </Grid>
                </Grid>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end' }}>
                <LoadingButton
                  loading={formik.isSubmitting}
                  startIcon={<SaveIcon />}
                  type="submit"
                  variant="contained"
                >
                  Save Appearance
                </LoadingButton>
              </CardActions>
            </Card>
          </form>
        </Grid>
      </Grid>
    </CenterLayout>
  );
};
